import React, { useState, useEffect, lazy } from 'react';
import { connect } from 'react-redux';
import TabWrapper from './TabWrapper';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Grid,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  CircularProgress
} from '@mui/material';
import { ethers } from 'ethers';
import { VestingAbi } from '../abis';
import { getUserCredits, getCreditHistoryByNote } from '../../../../../redux/actions/credit.action';
import {
  getVestingAddressesByDaoId,
  getEmailsByWalletAddressesInLPSale,
  sendEmailToDevPartner,
  getVestingAllocationsSummaryByVestingAddress,
} from '../../../../../redux/actions/dao.action';
import { inputStyles, buttonStyles } from './css/commonStyles';

const tableStyles = {
  backgroundColor: '#282f38',
  border: '1px solid #00EDC3',
  '& .MuiTableCell-root': {
    color: '#e7e3e3',
    borderColor: '#00EDC3',
  },
  '& .MuiTableHead-root': {
    backgroundColor: '#1a1f24',
  },
};

const Table = lazy(() => import('@mui/material/Table'));

const VestingTab = ({
  daoOrganizerAddress,
  chainId,
  currentDao,
  getVestingAddressesByDaoId,
  getEmailsByWalletAddressesInLPSale,
  sendEmailToDevPartner,
  getVestingAllocationsSummaryByVestingAddress,
}) => {
  const [addresses, setAddresses] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [currentAddress, setCurrentAddress] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');
  const [vestingContractAddress, setVestingContractAddress] = useState('');
  const [success, setSuccess] = useState('');
  const [vestingAddresses, setVestingAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchVestingAddresses = async () => {
      try {
        if (!currentDao || !currentDao._id) {
          setError('DAO ID is missing');
          return;
        }
        // Gọi API để lấy danh sách vesting addresses
        const data = await getVestingAddressesByDaoId(currentDao._id);
        console.log('Vesting addresses data:', data);
        // Kiểm tra nếu data không phải là mảng, gán giá trị mặc định là mảng rỗng
        setVestingAddresses(Array.isArray(data.data) ? data.data : []);
      } catch (error) {
        console.error('Error fetching vesting addresses:', error);
        // Gán giá trị mặc định là mảng rỗng khi có lỗi
        setVestingAddresses([]);
      }
    };

    fetchVestingAddresses();
  }, [currentDao]); // Chạy lại khi currentDao thay đổi

  useEffect(() => {
    const checkPermissions = async () => {
      setError('');
      setSuccess('');
      try {
        if (!window.ethereum) {
          setError('Please install MetaMask');
          return;
        }

        if (!vestingContractAddress) {
          setError('Please select a vesting contract address');
          return;
        }

        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const accounts = await provider.send('eth_requestAccounts', []);
        const currentAddress = accounts[0];
        setCurrentAddress(currentAddress);

        const contract = new ethers.Contract(vestingContractAddress, VestingAbi, provider);

        // Kiểm tra quyền admin
        const hasAdminRole = await contract.hasRole(
          ethers.utils.keccak256(ethers.utils.toUtf8Bytes('ADMIN_ROLE')),
          currentAddress,
        );
        setIsAdmin(hasAdminRole);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setError('Error checking permissions');
      }
    };

    checkPermissions();
  }, [vestingContractAddress]);

  const handleAddRow = () => {
    setAddresses([...addresses]);
    setAllocations([...allocations]);
  };

  const handleRemoveRow = (index) => {
    const newAddresses = addresses.filter((_, i) => i !== index);
    const newAllocations = allocations.filter((_, i) => i !== index);
    setAddresses(newAddresses);
    setAllocations(newAllocations);
  };

  const handleAddressChange = (index, value) => {
    const newAddresses = [...addresses];
    newAddresses[index] = value;
    setAddresses(newAddresses);
  };

  const handleAllocationChange = (index, value) => {
    const newAllocations = [...allocations];
    newAllocations[index] = value;
    setAllocations(newAllocations);
  };

  const handleAddAllocations = async () => {
    if (!isAdmin) {
      setError('You do not have permission to add allocations');
      return;
    }

    const validAddresses = addresses.filter((addr) => ethers.utils.isAddress(addr));
    const validAllocations = allocations
      .map((alloc) => {
        try {
          // Convert ether to wei
          return ethers.utils.parseEther(alloc.toString());
        } catch (e) {
          return null;
        }
      })
      .filter((alloc) => alloc !== null);

    if (validAddresses.length !== addresses.length || validAllocations.length !== allocations.length) {
      setError('Invalid addresses or allocations');
      return;
    }

    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(vestingContractAddress, VestingAbi, signer);

      const tx = await contract.addAllocations(validAddresses, validAllocations);
      await tx.wait();

      setSuccess('Allocations added successfully');
      setError('');
    } catch (error) {
      console.error('Error adding allocations:', error);
      setError(`Error adding allocations: ${error.message}`);
      setSuccess('');
    }
  };

  // Load allocations from backend aggregated by vesting address
  const handleLoadAllocations = async () => {
    try {
      setError('');
      setSuccess('');
      if (!vestingContractAddress) {
        setError('Please select a vesting contract address');
        return;
      }
      setIsLoading(true);

      const response = await getVestingAllocationsSummaryByVestingAddress(vestingContractAddress);
      const list = Array.isArray(response.data) ? response.data : [];

      if (list.length === 0) {
        setSuccess('No allocations found for this vesting address');
      }

      // Populate addresses and allocations (allocations assumed in LP units)
      setAddresses(list.map((i) => i.walletAddress));
      setAllocations(list.map((i) => (i.totalLP != null ? String(i.totalLP) : '0')));
    } catch (e) {
      setError(e.message || 'Failed to load allocations');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <TabWrapper daoOrganizerAddress={daoOrganizerAddress} chainId={chainId}>
      <Box sx={{ p: 4 }}>
        <Typography
          variant="h6"
          sx={{ mb: 3, pb: 2, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          Vesting - Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
            Select Vesting Address
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="vesting-select-label" sx={{ color: '#00EDC3 !important' }}>
              Vesting Contract Address
            </InputLabel>
            <Select
              labelId="vesting-select-label"
              value={vestingContractAddress}
              label="Vesting Contract Address"
              onChange={(e) => setVestingContractAddress(e.target.value)}
              sx={{
                color: '#e7e3e3',
                backgroundColor: '#282f38',
                borderRadius: '8px',
                '& .MuiSelect-icon': {
                  color: '#00EDC3',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#00EDC3',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#00EDC3',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#00EDC3',
                },
              }}
            >
              {vestingAddresses.map((item) => (
                <MenuItem key={item.vestingAddress} value={item.vestingAddress}>
                  {item.name} - {item.vestingAddress}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {vestingContractAddress && (
            <Typography variant="body2" sx={{ mt: 0.5, color: '#e7e3e3' }}>
              Selected vesting address: {vestingContractAddress}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontSize: '14px', fontFamily: 'Questrial, normal', color: '#e7e3e3' }}>
            Current Address: {currentAddress}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '14px', fontFamily: 'Questrial, normal', color: '#e7e3e3' }}>
            Permissions: {isAdmin ? 'Admin' : 'None'}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
                Add Allocations
              </Typography>

              <TableContainer component={Paper} sx={tableStyles}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Wallet Address</TableCell>
                      <TableCell>Total LP purchased</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {addresses.map((address, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {address}
                        </TableCell>
                        <TableCell>
                          {allocations[index]}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button variant="contained" onClick={handleLoadAllocations} sx={{...buttonStyles, width: '220px'}} disabled={!vestingContractAddress}>
                  {isLoading ? <CircularProgress size={24}/> : 'Campaign transactions'}
                </Button>
                <Button variant="contained" onClick={handleAddAllocations} sx={buttonStyles} disabled={!vestingContractAddress || addresses.length === 0}>
                  Submit Allocations
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </TabWrapper>
  );
};

const mapStateToProps = (state) => ({
  currentDao: state.dao?.currentDao || null,
});

const mapDispatchToProps = {
  getVestingAddressesByDaoId,
  getEmailsByWalletAddressesInLPSale,
  sendEmailToDevPartner,
  getVestingAllocationsSummaryByVestingAddress,
};

export default connect(mapStateToProps, mapDispatchToProps)(VestingTab);
